import React, { useState, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import FlightRangeList from '../Components/FlightRange/FlightRangeList';
import Partners from '../Components/Partners/Partners';
import InfoModal from '../Components/Modals/InfoModal';
import DownloadModal from '../Components/Modals/DownloadModal';
import { useUserRole } from '../hooks/useUserRole';
import './FlightRange.scss';

const FlightRange: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const infoButtonRef = useRef<HTMLButtonElement>(null);
  const downloadButtonRef = useRef<HTMLButtonElement>(null);
  const { isAdmin } = useUserRole();

  const [activeTab, setActiveTab] = useState<string>(
    location.pathname.includes('partners')
      ? 'partners'
      : location.pathname.includes('admin')
        ? 'admin'
        : 'flightRanges'
  );
  const [isInfoModalOpen, setIsInfoModalOpen] = useState<boolean>(false);
  const [isDownloadModalOpen, setIsDownloadModalOpen] = useState<boolean>(false);

  const handleTabChange = (tab: string) => {
    setActiveTab(tab);
    navigate(tab === 'partners' ? '/partners' : tab === 'admin' ? '/flight-ranges/admin' : '/flight-ranges');
  };

  const handleOpenInfoModal = () => {
    setIsInfoModalOpen(true);
  };

  const handleCloseInfoModal = () => {
    setIsInfoModalOpen(false);
    if (infoButtonRef.current) {
      infoButtonRef.current.focus();
    }
  };

  const handleOpenDownloadModal = () => {
    setIsDownloadModalOpen(true);
  };

  const handleCloseDownloadModal = () => {
    setIsDownloadModalOpen(false);
    if (downloadButtonRef.current) {
      downloadButtonRef.current.focus();
    }
  };

  return (
    <div className="flight-range" data-testid="flight-range">
      <div className="flight-range__header">
        <nav className="flight-range__tabs" role="navigation">
          <Link
            to="/flight-ranges"
            className={`flight-range__tab ${activeTab === 'flightRanges' ? 'active' : ''}`}
            onClick={() => handleTabChange('flightRanges')}
            aria-current={activeTab === 'flightRanges' ? 'page' : undefined}
          >
            Flight ranges
          </Link>
          <Link
            to="/partners"
            className={`flight-range__tab ${activeTab === 'partners' ? 'active' : ''}`}
            onClick={() => handleTabChange('partners')}
            aria-current={activeTab === 'partners' ? 'page' : undefined}
          >
            Partners
          </Link>
          {isAdmin && (
            <Link
              to="/flight-ranges/admin"
              className={`flight-range__tab ${activeTab === 'admin' ? 'active' : ''}`}
              onClick={() => handleTabChange('admin')}
              aria-current={activeTab === 'admin' ? 'page' : undefined}
            >
              Modify
            </Link>
          )}
        </nav>

        <div className="flight-range__actions">
          <button
            ref={infoButtonRef}
            className="flight-range__info-button"
            onClick={handleOpenInfoModal}
            aria-label="Show information"
            aria-haspopup="dialog"
          >
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M8 16C12.4183 16 16 12.4183 16 8C16 3.58172 12.4183 0 8 0C3.58172 0 0 3.58172 0 8C0 12.4183 3.58172 16 8 16Z" fill="#0074CA" fillOpacity="0.1"/>
              <path d="M8 3.5C8.41421 3.5 8.75 3.83579 8.75 4.25C8.75 4.66421 8.41421 5 8 5C7.58579 5 7.25 4.66421 7.25 4.25C7.25 3.83579 7.58579 3.5 8 3.5Z" fill="#0074CA"/>
              <path d="M8.75 12V7H7.25V12H8.75Z" fill="#0074CA"/>
            </svg>
          </button>
          <button
            ref={downloadButtonRef}
            className="flight-range__download-button"
            onClick={handleOpenDownloadModal}
            aria-label="Open download options"
            aria-haspopup="dialog"
          >
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M14 5H10V1H6V5H2L8 11L14 5ZM2 13V15H14V13H2Z" fill="#0074CA"/>
            </svg>
            Download xls
          </button>
        </div>
      </div>

      <div className="flight-range__content">
        {activeTab === 'flightRanges' ? <FlightRangeList /> : <Partners />}
      </div>

      <InfoModal
        isOpen={isInfoModalOpen}
        onClose={handleCloseInfoModal}
        title="Info"
        content={
          <>
            <p className="info-modal__description">
              This tool displays flight ranges, and partner airlines.
            </p>
            <div className="info-modal__section">
              <h3>Activity</h3>
              <p>Future activity for flight ranges are shown in red or green.</p>
              <p>
                <span className="text-success">Effective date</span> = Date the flight will Sell by or Operate by.
              </p>
              <p>
                <span className="text-danger">Discontinued date</span> = Last date the flight will be operational.
              </p>
            </div>
            <div className="info-modal__section">
              <h3>Discontinued flights</h3>
              <p>Any flights discontinued before today's date are not shown.</p>
            </div>
          </>
        }
      />

      <DownloadModal
        isOpen={isDownloadModalOpen}
        onClose={handleCloseDownloadModal}
        activeTab={activeTab}
      />
    </div>
    
  );
};

export default FlightRange;